import {timezoneSafeTimestamp} from '../core/time.js';
function required(value,label,max=180){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function positiveMinutes(value,label='durationMinutes'){
  const number=Number(value);
  if(!Number.isInteger(number)||number<1||number>365*24*60){
    throw new TypeError(label+' must be a positive whole number of minutes');
  }
  return number;
}

function optionalTimestamp(value,label){
  if(value==null||value==='')return null;
  return timezoneSafeTimestamp(value,label);
}

function blockOrder(left,right){
  const leftStart=left.startAt==null?Number.POSITIVE_INFINITY:Date.parse(left.startAt);
  const rightStart=right.startAt==null?Number.POSITIVE_INFINITY:Date.parse(right.startAt);
  return leftStart-rightStart
    ||String(left.lane).localeCompare(String(right.lane))
    ||Number(left.sequence||0)-Number(right.sequence||0)
    ||String(left.id).localeCompare(String(right.id));
}

function generatedBlock(block){
  return Object.freeze({
    id:required(block.id,'block.id'),
    kind:String(block.kind||''),
    label:String(block.label||''),
    lane:String(block.lane||'process'),
    sequence:Number(block.sequence||0),
    itemId:block.itemId??null,
    stageType:block.stageType??null,
    sourceRef:block.sourceRef??null,
    durationKind:block.durationKind,
    origin:'generated',
    generated:Object.freeze({
      startAt:block.startAt??null,
      endAt:block.endAt??null,
      durationMinutes:block.durationMinutes??null
    }),
    startAt:block.startAt??null,
    endAt:block.endAt??null,
    durationMinutes:block.durationMinutes??null,
    manualFields:Object.freeze([])
  });
}

function safetyFinding(finding){
  return ['incompatible','unknown','review'].includes(String(finding?.status||''));
}

function cloneCompatibility(value){
  if(!value)return Object.freeze({
    status:'unknown',
    findings:Object.freeze([]),
    requiresReview:false
  });
  return Object.freeze({
    status:String(value.status||'unknown'),
    findings:Object.freeze([...(value.findings||[])].map(item=>Object.freeze({...item}))),
    requiresReview:Boolean(value.requiresReview)
  });
}

function recomputeReview(review,blocks,{acknowledgedFindingCodes=review.acknowledgedFindingCodes}={}){
  const compatibility=cloneCompatibility(review.compatibility);
  const requiredCodes=compatibility.findings
    .filter(safetyFinding)
    .map(item=>String(item.code));
  const acknowledged=new Set([...(acknowledgedFindingCodes||[])].map(String));
  const missingAcknowledgements=requiredCodes.filter(code=>!acknowledged.has(code));
  const process=blocks.filter(block=>block.lane==='process'&&block.kind!=='buffer');
  const processStarts=process.map(block=>block.startAt).filter(Boolean).map(Date.parse);
  const processEnds=process.map(block=>block.endAt).filter(Boolean).map(Date.parse);
  const complete=blocks.every(block=>
    block.kind==='buffer'
      ?Boolean(block.startAt&&block.endAt)
      :Boolean(block.startAt&&block.endAt&&block.durationMinutes)
  );
  return Object.freeze({
    generatedSchedule:review.generatedSchedule,
    compatibility,
    blocks:Object.freeze([...blocks].sort(blockOrder)),
    acknowledgedFindingCodes:Object.freeze([...acknowledged]),
    missingAcknowledgements:Object.freeze(missingAcknowledgements),
    complete,
    canApply:complete&&missingAcknowledgements.length===0&&processStarts.length>0&&processEnds.length>0,
    processStartAt:processStarts.length?new Date(Math.min(...processStarts)).toISOString():null,
    processEndAt:processEnds.length?new Date(Math.max(...processEnds)).toISOString():null,
    lastImpact:review.lastImpact??null
  });
}

export function createPlannerReview(schedule,{compatibility=null}={}){
  if(!schedule?.request||!Array.isArray(schedule.blocks)){
    throw new TypeError('Generated Planner schedule is required');
  }
  const base=Object.freeze({
    generatedSchedule:schedule,
    compatibility:cloneCompatibility(compatibility),
    blocks:Object.freeze(schedule.blocks.map(generatedBlock)),
    acknowledgedFindingCodes:Object.freeze([]),
    missingAcknowledgements:Object.freeze([]),
    complete:false,
    canApply:false,
    processStartAt:null,
    processEndAt:null,
    lastImpact:null
  });
  return recomputeReview(base,[...base.blocks]);
}

function resolveOverride(block,changes={}){
  const manualFields=[];
  let start=block.startAt;
  let end=block.endAt;
  let duration=block.durationMinutes;

  const hasStart=Object.hasOwn(changes,'startAt');
  const hasEnd=Object.hasOwn(changes,'endAt');
  const hasDuration=Object.hasOwn(changes,'durationMinutes');

  if(hasStart){
    start=optionalTimestamp(changes.startAt,'startAt');
    manualFields.push('startAt');
  }
  if(hasEnd){
    end=optionalTimestamp(changes.endAt,'endAt');
    manualFields.push('endAt');
  }
  if(hasDuration){
    duration=positiveMinutes(changes.durationMinutes);
    manualFields.push('durationMinutes');
  }

  if(hasStart&&hasEnd){
    if(!start||!end)throw new TypeError('Manual startAt and endAt must both be set');
    const elapsed=Math.round((Date.parse(end)-Date.parse(start))/60000);
    if(elapsed<1)throw new TypeError('Manual block end must be after start');
    if(hasDuration&&duration!==elapsed){
      throw new TypeError('Manual duration must match start/end interval');
    }
    duration=elapsed;
  }else if(hasStart){
    if(!start)throw new TypeError('Manual startAt is required');
    if(duration==null)throw new TypeError('durationMinutes is required with a manual start');
    end=new Date(Date.parse(start)+duration*60000).toISOString();
  }else if(hasEnd){
    if(!end)throw new TypeError('Manual endAt is required');
    if(duration==null)throw new TypeError('durationMinutes is required with a manual end');
    start=new Date(Date.parse(end)-duration*60000).toISOString();
  }else if(hasDuration){
    if(end){
      start=new Date(Date.parse(end)-duration*60000).toISOString();
    }else if(start){
      end=new Date(Date.parse(start)+duration*60000).toISOString();
    }else{
      throw new TypeError('Manual duration needs a known start or end');
    }
  }else{
    throw new TypeError('At least one Planner block override is required');
  }

  return Object.freeze({startAt:start,endAt:end,durationMinutes:duration,manualFields});
}

function downstreamImpact(blocks,original,next){
  const sameLane=[...blocks]
    .filter(block=>block.lane===original.lane&&block.id!==original.id)
    .sort(blockOrder);
  const originalIndex=[...blocks].filter(block=>block.lane===original.lane).sort(blockOrder)
    .findIndex(block=>block.id===original.id);
  const downstream=sameLane.filter(block=>{
    const laneOrder=[...blocks].filter(item=>item.lane===original.lane).sort(blockOrder);
    return laneOrder.findIndex(item=>item.id===block.id)>originalIndex;
  });
  const generatedEnd=original.generated.endAt?Date.parse(original.generated.endAt):null;
  const nextEnd=next.endAt?Date.parse(next.endAt):null;
  const shiftMinutes=generatedEnd!=null&&nextEnd!=null
    ?Math.round((nextEnd-generatedEnd)/60000)
    :null;
  const immediate=downstream[0]||null;
  let boundary='unknown';
  let boundaryMinutes=null;
  if(immediate?.startAt&&next.endAt){
    boundaryMinutes=Math.round((Date.parse(immediate.startAt)-Date.parse(next.endAt))/60000);
    boundary=boundaryMinutes===0?'continuous':boundaryMinutes>0?'gap':'overlap';
  }
  return Object.freeze({
    blockId:original.id,
    lane:original.lane,
    shiftMinutes,
    affectedBlockIds:Object.freeze(downstream.map(block=>block.id)),
    boundary,
    boundaryMinutes
  });
}

export function overridePlannerBlock(review,blockId,changes={}){
  if(!review?.generatedSchedule||!Array.isArray(review.blocks)){
    throw new TypeError('Planner review is required');
  }
  const id=required(blockId,'blockId');
  const original=review.blocks.find(block=>block.id===id);
  if(!original)throw new Error('PLANNER_BLOCK_NOT_FOUND');

  const resolved=resolveOverride(original,changes);
  const next=Object.freeze({
    ...original,
    origin:'manual',
    startAt:resolved.startAt,
    endAt:resolved.endAt,
    durationMinutes:resolved.durationMinutes,
    manualFields:Object.freeze([...new Set([...original.manualFields,...resolved.manualFields])])
  });
  const blocks=review.blocks.map(block=>block.id===id?next:block);
  const impact=downstreamImpact(blocks,original,next);
  return recomputeReview({...review,lastImpact:impact},blocks);
}

export function resetPlannerReview(review){
  return createPlannerReview(review.generatedSchedule,{compatibility:review.compatibility});
}

export function acknowledgePlannerFinding(review,code,acknowledged=true){
  const value=required(code,'finding code',100);
  const allowed=new Set(
    review.compatibility.findings.filter(safetyFinding).map(item=>String(item.code))
  );
  if(!allowed.has(value))throw new Error('PLANNER_FINDING_NOT_ACKNOWLEDGEABLE');
  const next=new Set(review.acknowledgedFindingCodes||[]);
  if(acknowledged)next.add(value);
  else next.delete(value);
  return recomputeReview(review,[...review.blocks],{
    acknowledgedFindingCodes:[...next]
  });
}

function snapshotBlock(block){
  return Object.freeze({
    id:block.id,
    kind:block.kind,
    lane:block.lane,
    sequence:block.sequence,
    sourceRef:block.sourceRef,
    itemId:block.itemId??null,
    stageType:block.stageType??null,
    origin:block.origin,
    manualFields:Object.freeze([...(block.manualFields||[])]),
    generated:Object.freeze({...block.generated}),
    applied:Object.freeze({
      startAt:block.startAt,
      endAt:block.endAt,
      durationMinutes:block.durationMinutes
    })
  });
}

export function plannerReviewApplication(review,{
  now=()=>new Date().toISOString(),
  plannerRunId=null
}={}){
  if(!review?.canApply)throw new Error('PLANNER_REVIEW_NOT_READY');
  const appliedAt=timezoneSafeTimestamp(now(),'appliedAt');
  const runId=String(plannerRunId??'').trim();
  if(runId&&!/^[a-zA-Z0-9_-]{8,80}$/.test(runId)){
    throw new TypeError('plannerRunId is invalid');
  }
  const snapshot=Object.freeze({
    snapshotVersion:1,
    ...(runId?{plannerRunId:runId}:{}),
    targetReadyAt:review.generatedSchedule.request.targetReadyAt,
    timeZone:review.generatedSchedule.request.timeZone,
    bufferMinutes:review.generatedSchedule.request.bufferMinutes,
    appliedAt,
    compatibilityStatus:review.compatibility.status,
    acknowledgedFindingCodes:Object.freeze([...(review.acknowledgedFindingCodes||[])]),
    blocks:Object.freeze(review.blocks.map(snapshotBlock))
  });
  return Object.freeze({
    plannedStartAt:review.processStartAt,
    plannedEndAt:review.processEndAt,
    plannerScheduleSnapshot:snapshot
  });
}
